import type { StructureOccupancyNode } from "../api/rest";

/** One node flattened for rendering, tagged with its tree depth for indentation.
 *  `hasChildren` is the node's own shape, not what the filter chose to emit — a
 *  collapsed branch must still show that there is something under it. */
export interface StructureOccupancyRow {
  node: StructureOccupancyNode;
  depth: number;
  hasChildren: boolean;
}

/**
 * Flatten the rollup tree to indented rows in pre-order (parents before children), each
 * tagged with its depth. Subtrees with no occupiable spaces are dropped: a rollup sums up,
 * so a node with {@code occupiableSpaces === 0} has no occupiable descendants either — an
 * entirely non-occupiable branch (transition / office / operator-curated) is noise in an
 * occupancy view, and dropping it never orphans an occupiable child.
 */
export function flattenOccupancyTree(
  roots: StructureOccupancyNode[],
  isOpen: (node: StructureOccupancyNode, depth: number) => boolean = () => true,
  depth = 0,
): StructureOccupancyRow[] {
  const rows: StructureOccupancyRow[] = [];
  for (const node of roots) {
    if (node.occupancy.occupiableSpaces === 0) continue;
    const children = node.children.filter((c) => c.occupancy.occupiableSpaces > 0);
    rows.push({ node, depth, hasChildren: children.length > 0 });
    // ADR 233: a closed branch contributes no rows. Fully expanded, JSL's graph
    // is ~4 650 rows and ~46 500 DOM nodes — 91% of the whole report's DOM, for
    // a tree nobody reads past the aisles.
    if (isOpen(node, depth)) rows.push(...flattenOccupancyTree(children, isOpen, depth + 1));
  }
  return rows;
}
